import styled from 'styled-components';
import { Spacing24, Spacing32 } from 'autogen/design-tokens/tokens';
import type { ProductPrelaunchBannerModel } from 'autogen/swagger/all';
import { use_LoginInfoQuery } from 'autogen/swagger/all/hooks';
import { ButtonWithSpinner } from 'components/Button';
import LinkActionButton from 'components/Button/LinkActionButton';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import { Typography } from 'components/generic/typography';
import useLoginInfoQuickView from 'components/login/shared/hooks/useLoginInfoQuickView';
import format from 'helpers/translations';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import { breakpoint, sizes } from 'variables';

const Container = styled.div`
    display: grid;
    gap: ${Spacing24};
    background-color: #f9cdce;
    padding: ${Spacing32};
    position: relative;

    ${breakpoint.up(sizes.md)} {
        grid-template-columns: minmax(254px, 20%) 1fr;
    }

    ${breakpoint.up(sizes.lg)} {
        grid-template-columns: minmax(254px, 20%) 1fr minmax(auto, 180px);
    }
`;
const ActionContainer = styled.div`
    display: grid;
    gap: ${Spacing24};
`;

const DescriptionContainer = styled.div``;

const ImageContainer = styled.div`
    display: none;
    position: absolute;
    right: 24px;
    bottom: 0;
    height: 100%;

    & > picture {
        display: flex;
        height: 100%;
        width: 100%;
    }

    ${breakpoint.up(sizes.lg)} {
        display: block;
    }
`;

export default function PrelaunchBanner({ model }: { model: ProductPrelaunchBannerModel }) {
    const { header, productInfo, signupInfo, reviewButton, teaserImage } = model;
    const {
        link: {
            text,
            link: { linkAction, title, target }
        },
        loginRequest
    } = reviewButton;

    const { refetch: fetchLoginInfo } = use_LoginInfoQuery(
        { ...loginRequest, infoType: 'WriteReview' }, // why is backend providing the type "General" when they should provide "WriteReview". Anyway we overwrite it here.
        { enabled: false }
    );

    const [, toggle] = useLoginInfoQuickView();
    const isAuthenticated = useIsAuthenticated();

    return (
        <Container>
            <ActionContainer>
                <Typography.Headline size="M">{header}</Typography.Headline>

                {isAuthenticated ? (
                    <LinkActionButton action={linkAction} size="lg" title={title} target={target}>
                        {text}
                    </LinkActionButton>
                ) : (
                    <ButtonWithSpinner
                        size="lg"
                        onClick={() =>
                            fetchLoginInfo().then(({ data: loginInfo }) => toggle({ isOpen: true, loginInfo }))
                        }
                    >
                        {text}
                    </ButtonWithSpinner>
                )}
            </ActionContainer>

            <DescriptionContainer>
                <Typography.Body size="L">{format(productInfo)}</Typography.Body>
                {signupInfo && (
                    <Typography.Body as="span" size="L">
                        {format(signupInfo)}
                    </Typography.Body>
                )}
            </DescriptionContainer>

            <ImageContainer>{teaserImage && <PictureDeprecated {...teaserImage} />}</ImageContainer>
        </Container>
    );
}
